"use client";

import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import "../../components/Common/AdminDetailModal.css";
import "./CampaignApplicationDetailsModal.css";

export default function CampaignApplicationDetailsModal({
  modal_center,
  setmodal_center,
  tog_center,
  application,
  onStatusUpdate,
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper functions to safely render values
  const safeString = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "object") return "N/A";
    return String(value);
  };

  const safeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "object") return 0;
    return Number(value) || 0;
  };

  const updateApplicationStatus = async (newStatus) => {
    if (!application || !application.id) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/campaign-applications/${application.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(`Application ${newStatus} successfully`, {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
        });

        // Update the application in parent component
        if (onStatusUpdate) {
          onStatusUpdate(application.id, newStatus);
        }

        // Close modal
        tog_center();
      } else {
        toast.error(
          response.data.message || "Failed to update application status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating application status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "warning", text: "Pending" },
      approved: { color: "success", text: "Approved" },
      rejected: { color: "danger", text: "Rejected" },
      completed: { color: "primary", text: "Completed" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(safeNumber(price));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!application) return null;

  const isPending = application.status === "pending";

  const profileImageSrc = application.influencer?.profile_image
    ? `https://api.tafaseelapp.com/storage/${application.influencer.profile_image}`
    : null;

  return (
    <Modal
      isOpen={modal_center}
      toggle={tog_center}
      centered
      size="lg"
      className="admin-detail-modal application-details-modal"
    >
      <ModalHeader toggle={tog_center} className="border-0">
        <div className="adm-header-inner d-flex align-items-center flex-wrap gap-2">
          <i className="ri-file-user-line" aria-hidden />
          <span>Campaign Application Details</span>
          {isPending && (
            <Badge color="warning" className="ms-0">
              <i className="ri-time-line me-1"></i>
              Needs Review
            </Badge>
          )}
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="application-details">
          <div className="adm-hero">
            {profileImageSrc ? (
              <img
                src={profileImageSrc}
                alt={safeString(application.influencer?.name)}
                className="adm-hero-thumb"
              />
            ) : (
              <div
                className="adm-hero-thumb d-flex align-items-center justify-content-center bg-light text-secondary fw-semibold"
                aria-hidden
              >
                {(safeString(application.influencer?.name) || "?")
                  .charAt(0)
                  .toUpperCase()}
              </div>
            )}
            <div className="flex-grow-1 min-w-0">
              <h5 className="adm-hero-title mb-1">
                {safeString(application.influencer?.name)}
              </h5>
              <p className="adm-hero-meta mb-2">
                {safeString(application.influencer?.email)}
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {getStatusBadge(application.status)}
                <Badge color="light" className="text-dark border">
                  Application #{safeString(application.id)}
                </Badge>
              </div>
            </div>
            <div className="application-details__applied text-md-end ms-md-auto pt-2 pt-md-0">
              <div className="text-uppercase small fw-bold text-primary mb-0">
                Applied on
              </div>
              <div className="text-muted small">
                {formatDate(application.applied_at || application.created_at)}
              </div>
            </div>
          </div>

          <div className="adm-section">
            <div className="adm-section-head">
              <i className="ri-megaphone-line" aria-hidden />
              Campaign information
            </div>
            <Row className="g-3 align-items-start">
              <Col md={8}>
                <h6 className="fw-bold text-dark mb-2">
                  {safeString(application.campaign?.title)}
                </h6>
                <p className="text-muted small mb-2 mb-md-3">
                  {safeString(application.campaign?.description)}
                </p>
                <div className="d-flex flex-wrap gap-2">
                  <Badge color="info">
                    {safeString(application.campaign?.category?.name)}
                  </Badge>
                  <Badge color="secondary">
                    {safeString(application.campaign?.location)}
                  </Badge>
                </div>
              </Col>
              <Col md={4} className="text-md-end">
                <div className="application-details__budget d-inline-block text-md-end">
                  <div className="fw-bold text-success fs-5 mb-0">
                    {formatPrice(application.campaign?.price)}
                  </div>
                  <small className="text-muted">Campaign budget</small>
                </div>
              </Col>
            </Row>
          </div>

          <Row className="g-3">
            <Col md={6}>
              <div className="adm-section h-100">
                <div className="adm-section-head">
                  <i className="ri-information-line" aria-hidden />
                  Application information
                </div>
                <dl className="adm-kv mb-0">
                  <dt>Application ID</dt>
                  <dd>#{safeString(application.id)}</dd>
                  <dt>Status</dt>
                  <dd>{getStatusBadge(application.status)}</dd>
                  <dt>Applied</dt>
                  <dd>
                    {formatDateTime(
                      application.applied_at || application.created_at
                    )}
                  </dd>
                  {application.reviewed_at && (
                    <>
                      <dt>Reviewed</dt>
                      <dd>{formatDateTime(application.reviewed_at)}</dd>
                    </>
                  )}
                  <dt>Last updated</dt>
                  <dd>{formatDateTime(application.updated_at)}</dd>
                </dl>
              </div>
            </Col>
            <Col md={6}>
              <div className="adm-section h-100">
                <div className="adm-section-head">
                  <i className="ri-user-star-line" aria-hidden />
                  Influencer information
                </div>
                <dl className="adm-kv mb-0">
                  <dt>Name</dt>
                  <dd>{safeString(application.influencer?.name)}</dd>
                  <dt>Email</dt>
                  <dd>{safeString(application.influencer?.email)}</dd>
                  {application.influencer?.bio && (
                    <>
                      <dt>Bio</dt>
                      <dd>{safeString(application.influencer.bio)}</dd>
                    </>
                  )}
                  <dt>Followers</dt>
                  <dd>
                    {safeNumber(
                      application.influencer?.followers_count
                    ).toLocaleString()}
                  </dd>
                  <dt>Engagement rate</dt>
                  <dd>{safeNumber(application.influencer?.engagement_rate)}%</dd>
                </dl>
              </div>
            </Col>
          </Row>

          {application.message && (
            <div className="adm-section">
              <div className="adm-section-head">
                <i className="ri-message-line" aria-hidden />
                Application message
              </div>
              <div className="application-details__prose rounded border border-light bg-white p-3">
                <p className="mb-0 small">{safeString(application.message)}</p>
              </div>
            </div>
          )}

          {application.business_notes && (
            <div className="adm-section">
              <div className="adm-section-head">
                <i className="ri-sticky-note-line" aria-hidden />
                Business notes
              </div>
              <div className="application-details__notes rounded p-3">
                <p className="mb-0 small">
                  {safeString(application.business_notes)}
                </p>
              </div>
            </div>
          )}

          <div className="adm-section">
            <div className="adm-section-head">
              <i className="ri-building-line" aria-hidden />
              Business information
            </div>
            <dl className="adm-kv mb-0">
              <dt>Business name</dt>
              <dd>{safeString(application.campaign?.user?.name)}</dd>
              <dt>Email</dt>
              <dd>{safeString(application.campaign?.user?.email)}</dd>
              {application.campaign?.user?.phone && (
                <>
                  <dt>Phone</dt>
                  <dd>{safeString(application.campaign.user.phone)}</dd>
                </>
              )}
              <dt>Member since</dt>
              <dd>{formatDate(application.campaign?.user?.created_at)}</dd>
            </dl>
          </div>

          {application.campaign?.rules && (
            <div className="adm-section">
              <div className="adm-section-head">
                <i className="ri-file-list-line" aria-hidden />
                Campaign rules & requirements
              </div>
              <div className="application-details__rules rounded p-3">
                <p className="mb-0 small">
                  {safeString(application.campaign.rules)}
                </p>
              </div>
            </div>
          )}

          {application.influencer?.categories &&
            application.influencer.categories.length > 0 && (
              <div className="adm-section mb-0">
                <div className="adm-section-head">
                  <i className="ri-price-tag-line" aria-hidden />
                  Influencer categories
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {application.influencer.categories.map((category, index) => (
                    <Badge
                      key={index}
                      color="light"
                      className="text-primary border"
                    >
                      {safeString(category)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
        </div>
      </ModalBody>

      <ModalFooter className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-3">
        <div className="d-flex justify-content-between w-100 flex-wrap gap-3">
          {/* Status Actions */}
          {isPending ? (
            <div className="approval-actions">
              <h6 className="mb-2 text-warning">
                <i className="ri-time-line me-2"></i>
                Application Review Required:
              </h6>
              <div className="d-flex gap-2">
                <Button
                  color="success"
                  onClick={() => updateApplicationStatus("approved")}
                  disabled={isUpdating}
                >
                  <i className="ri-check-line me-1"></i>
                  {isUpdating ? "Approving..." : "Approve Application"}
                </Button>
                <Button
                  color="danger"
                  onClick={() => updateApplicationStatus("rejected")}
                  disabled={isUpdating}
                >
                  <i className="ri-close-line me-1"></i>
                  {isUpdating ? "Rejecting..." : "Reject Application"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="status-actions">
              <h6 className="mb-2">Update Status:</h6>
              <div className="d-flex gap-2 flex-wrap">
                {application.status !== "approved" && (
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => updateApplicationStatus("approved")}
                    disabled={isUpdating}
                  >
                    <i className="ri-check-line me-1"></i>
                    Approve
                  </Button>
                )}
                {application.status !== "rejected" && (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => updateApplicationStatus("rejected")}
                    disabled={isUpdating}
                  >
                    <i className="ri-close-line me-1"></i>
                    Reject
                  </Button>
                )}
                {application.status !== "completed" &&
                  application.status === "approved" && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => updateApplicationStatus("completed")}
                      disabled={isUpdating}
                    >
                      <i className="ri-check-double-line me-1"></i>
                      Mark Complete
                    </Button>
                  )}
                {application.status !== "pending" && (
                  <Button
                    color="warning"
                    size="sm"
                    onClick={() => updateApplicationStatus("pending")}
                    disabled={isUpdating}
                  >
                    <i className="ri-time-line me-1"></i>
                    Reset to Pending
                  </Button>
                )}
              </div>
            </div>
          )}
          <div>
            <Button color="secondary" onClick={tog_center}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
